import type { ReactNode } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Calculator, CircleDashed, ImagePlus, Lock, Paperclip } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useOptionSetsByIds } from '@/hooks/useOptionSets'
import { useProcessStepSummary } from '@/hooks/useProcessSteps'
import type { FormField } from './types'
import { PROCESS_ACCENT } from './field-config'
import { TextBlock } from '@/components/renderer/TextBlock'
import { FieldRow } from '@/components/renderer/FieldRow'
import { SYSTEM_LABEL_LAYOUT, type ResolvedLabelLayout } from '@/lib/fieldLayout'
import { cellKey } from '@/lib/tableForm'

/**
 * What the canvas draws for a field. Field types with a label row go through
 * the renderer's own `FieldRow`, so the label sits where the form will put it
 * (z8nrz7djyx). Help text stays written out under the control: the canvas is
 * not interactive, so the form's help tooltip could not be opened here.
 */
export function FieldPreview({
  field,
  layout = SYSTEM_LABEL_LAYOUT,
  stacked = false,
}: {
  field: FormField
  layout?: ResolvedLabelLayout
  stacked?: boolean
}) {
  const label = field.label || 'Untitled'
  // Under the whole row rather than in the control column, so a legacy row
  // still centres its label on the control alone, as the form does.
  const withHelp = (row: ReactNode) =>
    field.helpText ? (
      <div className='space-y-1.5'>
        {row}
        <p className='text-xs text-muted-foreground'>{field.helpText}</p>
      </div>
    ) : (
      row
    )
  // Unconditional hook call (React rules); empty array → no network request.
  // React Query dedupes across multiple FieldPreview instances for the same id.
  const fetchedSets = useOptionSetsByIds(
    field.optionsSource === 'optionSet' && field.optionSetId ? [field.optionSetId] : [],
  )
  const matchedSet =
    field.optionsSource === 'optionSet' && field.optionSetId
      ? fetchedSets.find(s => s.id === field.optionSetId)
      : undefined

  if (field.type === 'section') {
    return (
      <div className='pt-1 pb-0.5'>
        <h3 className='text-sm font-semibold'>{field.label || 'Section'}</h3>
        <Separator className='mt-1.5' />
      </div>
    )
  }

  if (field.type === 'text' || field.type === 'number' || field.type === 'date' || field.type === 'datetime') {
    return withHelp(
      <FieldRow
        label={label}
        required={field.required}
        layout={layout}
        stacked={stacked}
        controlClassName='flex flex-1 flex-col gap-1.5'
      >
        {({ id }) => (
          <>
            <Input
              id={id}
              type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : 'text'}
              placeholder={field.placeholder || ' '}
              tabIndex={-1}
              readOnly
            />
          </>
        )}
      </FieldRow>
    )
  }

  if (field.type === 'textarea') {
    return withHelp(
      <FieldRow
        label={label}
        required={field.required}
        layout={layout}
        stacked={stacked}
        tall
        controlClassName='flex flex-1 flex-col gap-1.5'
      >
        {({ id }) => (
          <>
            <Textarea id={id} placeholder={field.placeholder || ' '} rows={3} tabIndex={-1} readOnly />
          </>
        )}
      </FieldRow>
    )
  }

  if (field.type === 'select') {
    const options =
      field.optionsSource === 'optionSet' && field.optionSetId
        ? (matchedSet?.options ?? []).map(o => ({ id: o.id, label: o.label }))
        : field.options
    return withHelp(
      <FieldRow
        label={label}
        required={field.required}
        layout={layout}
        stacked={stacked}
        controlClassName='flex flex-1 flex-col gap-1.5'
      >
        {({ id }) => (
          <>
            <Select>
              <SelectTrigger id={id} tabIndex={-1}>
                <SelectValue placeholder={field.placeholder || 'Select…'} />
              </SelectTrigger>
              <SelectContent>
                {options.map(opt => (
                  <SelectItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </FieldRow>
    )
  }

  if (field.type === 'checkbox') {
    // Configured: the label column, as on the form. Legacy: box first, inline.
    if (!layout.legacy || stacked) {
      const side = !stacked && layout.position !== 'above'
      return withHelp(
        <FieldRow
          label={label}
          required={field.required}
          layout={layout}
          stacked={stacked}
          controlClassName='flex flex-1 flex-col gap-1.5'
        >
          {({ id }) => (
            <>
              <div className={cn('flex items-center', side && 'sm:min-h-10')}>
                <Checkbox id={id} tabIndex={-1} />
              </div>
              </>
          )}
        </FieldRow>
      )
    }
    return (
      <div className='flex items-start gap-2 py-1'>
        <Checkbox className='mt-0.5' tabIndex={-1} />
        <div>
          <Label className='text-sm font-normal'>
            {field.label || 'Untitled'}
            {field.required && <span className='text-destructive ml-1'>*</span>}
          </Label>
          {field.helpText && (
            <p className='text-xs text-muted-foreground'>{field.helpText}</p>
          )}
        </div>
      </div>
    )
  }

  if (field.type === 'switch') {
    return (
      <div className='flex items-center justify-between py-1'>
        <div>
          <Label className='text-sm'>
            {field.label || 'Untitled'}
            {field.required && <span className='text-destructive ml-1'>*</span>}
          </Label>
          {field.helpText && (
            <p className='text-xs text-muted-foreground'>{field.helpText}</p>
          )}
        </div>
        <Switch tabIndex={-1} />
      </div>
    )
  }

  if (field.type === 'signature') {
    return (
      <div className='space-y-1.5'>
        <Label className='text-sm'>
          {field.label || 'Signature'}
          {field.required && <span className='text-destructive ml-1'>*</span>}
        </Label>
        <div className='h-16 rounded-md border-2 border-dashed border-blue-200 bg-blue-50 flex items-center justify-center text-sm text-blue-400'>
          Sign here
        </div>
        {field.helpText && (
          <p className='text-xs text-muted-foreground'>{field.helpText}</p>
        )}
      </div>
    )
  }

  if (field.type === 'calculated') {
    return withHelp(
      <FieldRow
        label={field.label || 'Calculated'}
        layout={layout}
        stacked={stacked}
        controlClassName='flex flex-1 flex-col gap-1.5'
      >
        {({ id }) => (
          <>
            <Input
              id={id}
              readOnly
              value='—'
              className='bg-muted text-muted-foreground font-mono cursor-default'
              tabIndex={-1}
            />
            <p className='flex items-center gap-1.5 text-xs text-muted-foreground'>
              <span className='inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary'>
                <Calculator className='h-2.5 w-2.5' />
                Calculated
              </span>
              {field.formula ? 'Auto-calculated from other fields' : 'No formula set yet'}
            </p>
          </>
        )}
      </FieldRow>
    )
  }

  if (field.type === 'table') {
    const tc = field.tableConfig
    const cols = tc?.columns ?? []
    const rows = tc?.rows ?? []
    // Same header switches as the form's TableGrid; absent = shown.
    const showRowHeader = tc?.showRowHeader !== false
    const showColumnHeader = tc?.showColumnHeader !== false
    return (
      <div className='space-y-1.5'>
        <Label className='text-sm'>{field.label || 'Table'}</Label>
        {/* pointer-events-auto: the canvas card wraps previews in pointer-events-none,
            which would leave this horizontal scroll area inert and clip wide tables
            silently. The table has no interactive controls, and clicks still bubble
            to the card's select handler. */}
        <div className='pointer-events-auto overflow-x-persistent rounded border text-xs'>
          <table className='w-full border-collapse'>
            {showColumnHeader && (
              <thead>
                <tr>
                  {showRowHeader && (
                    <th className='border bg-table-header px-2 py-1 text-left font-semibold text-table-header-foreground'>
                      {tc?.rowHeaderLabel || '—'}
                    </th>
                  )}
                  {cols.map(col => (
                    <th key={col.id} className='border bg-table-header px-2 py-1 text-left font-semibold text-table-header-foreground'>
                      {col.label || 'Col'}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  {showRowHeader && (
                    <th className='border bg-muted/20 px-2 py-1 text-left font-medium'>
                      {row.label || 'Row'}
                    </th>
                  )}
                  {cols.map(col =>
                    tc?.cells?.[cellKey(row.id, col.id)]?.disabled ? (
                      <td key={col.id} aria-disabled='true' title='Not applicable' className='border bg-muted px-2 py-1' />
                    ) : (
                      <td key={col.id} className='border px-2 py-1 text-muted-foreground'>—</td>
                    ),
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={Math.max(1, cols.length + (showRowHeader ? 1 : 0))} className='border px-2 py-2 text-center text-muted-foreground'>
                    No rows configured
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {(cols.length === 0 || rows.length === 0) && (
          <p className='text-xs text-muted-foreground'>Configure columns and rows in the panel →</p>
        )}
      </div>
    )
  }

  // Text block (z8nrz7djyy): drawn with the renderer's own component and no
  // label, so the canvas shows exactly what the form will. An empty one gets a
  // placeholder HERE only — on the form it renders nothing — so the author can
  // still see and select it.
  if (field.type === 'description') {
    return field.constantText?.trim() ? (
      <TextBlock text={field.constantText} />
    ) : (
      <p className='rounded border border-dashed px-3 py-2 text-sm italic text-muted-foreground'>
        Empty text block — write its text in the panel →
      </p>
    )
  }

  if (field.type === 'image') {
    return withHelp(
      <FieldRow
        label={field.label || 'Image'}
        required={field.required}
        layout={layout}
        stacked={stacked}
        tall
        controlClassName='flex flex-1 flex-col gap-1.5'
      >
        {({ id }) => (
          <>
            {/* The same 112×80 box the renderer draws, so the card shows the space a
                picture will actually take on the form rather than a generic input. */}
            <div
              id={id}
              className='flex h-20 w-28 flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-muted/20 text-xs text-muted-foreground'
            >
              <ImagePlus className='h-4 w-4' />
              <span>Add image</span>
            </div>
          </>
        )}
      </FieldRow>
    )
  }

  if (field.type === 'attachment') {
    return withHelp(
      <FieldRow
        label={field.label || 'Attachment'}
        required={field.required}
        layout={layout}
        stacked={stacked}
        tall
        controlClassName='flex min-w-0 flex-1 flex-col gap-1.5'
      >
        {({ id }) => (
          <>
            <div
              id={id}
              className='flex items-center gap-2 rounded-lg border border-dashed bg-muted/20 px-3 py-2 text-xs text-muted-foreground'
            >
              <Paperclip className='h-4 w-4' />
              <span>Add files</span>
              <span className='ml-auto'>PDF, JPEG or PNG</span>
            </div>
          </>
        )}
      </FieldRow>
    )
  }

  if (field.type === 'process') {
    return <ProcessPreview field={field} />
  }

  // Review (z8nrz7dt41): drawn as the grey Review area the form will show at
  // its bottom, so the author sees what it becomes rather than an input.
  if (field.type === 'review') {
    return (
      <div className='space-y-1.5'>
        <Label className='text-sm font-semibold'>{field.label || 'Review'}</Label>
        <div className='flex flex-wrap items-center gap-4 rounded-lg border bg-muted/60 px-3 py-2 text-xs text-muted-foreground'>
          <span>
            Assigned <span className='ml-1 rounded border bg-background px-1.5 py-0.5'>Select reviewer</span>
          </span>
          <span>Status: Unassigned</span>
          <span className='inline-flex items-center gap-1'>
            <Lock className='h-3 w-3' /> Electronic Signature
          </span>
        </div>
        <p className='text-xs text-muted-foreground'>
          {field.helpText || 'Appears at the bottom of the form; signed once the form is completed.'}
        </p>
      </div>
    )
  }

  return null
}

/**
 * Canvas preview for a Process step.
 *
 * Previously there was no branch for `process` at all, so it fell through to
 * `return null` and the card rendered blank — a builder could not tell one
 * process step from another, or from an empty card.
 *
 * Its own component so the summary query runs ONLY for process fields; a hook in
 * `FieldPreview` would fire for every field on the canvas.
 */
function ProcessPreview({ field }: { field: FormField }) {
  // Null when the step has no connection yet (the endpoint 404s → requestOrNull).
  const { data: summary } = useProcessStepSummary(field.id || null)

  const parts = summary
    ? [
        summary.equipmentRequirements > 0 &&
          `${summary.equipmentRequirements} equipment`,
        summary.inventoryComponents > 0 && `${summary.inventoryComponents} material${summary.inventoryComponents === 1 ? '' : 's'}`,
        summary.equipmentTimePerRun > 0 && `${summary.equipmentTimePerRun}h run`,
        summary.personHoursPerRun > 0 && `${summary.personHoursPerRun}h staffing`,
      ].filter((p): p is string => typeof p === 'string')
    : []

  return (
    <div className='space-y-1.5'>
      <Label className='text-sm'>{field.label || 'Process step'}</Label>
      <div
        className={cn(
          'flex items-start gap-2 rounded border border-dashed px-3 py-2',
          PROCESS_ACCENT.box,
        )}
      >
        <CircleDashed className={cn('mt-0.5 h-4 w-4 shrink-0', PROCESS_ACCENT.icon)} />
        <div className='min-w-0'>
          <p className='truncate text-sm font-medium'>{field.label || 'Untitled process step'}</p>
          <p className='truncate text-xs text-muted-foreground'>
            {parts.length > 0
              ? parts.join(' · ')
              : 'No equipment, materials or staffing configured yet'}
          </p>
          {field.helpText && (
            <p className='truncate text-xs text-muted-foreground'>{field.helpText}</p>
          )}
        </div>
      </div>
    </div>
  )
}
